// Groups the flat, display-ordered layer list into timeline rows. Purely a
// view transform — the flat list stays the data/playback model. A row exists
// iff at least one layer carries its trackId; layers without one (legacy
// projects, text/shape/etc.) each get their own solo row.

// scene-audio (narration) rows can also be merged with each other — their
// trackId lives on the sound object (soundList) instead of box.timeline, but
// the grouping/join mechanics are identical. Cross-type joins are still
// impossible because the type is part of the row key and of the drag session's
// compatibility check.
export const GROUPABLE_TYPES = new Set(["image", "video", "audio", "text", "scene-audio"]);

// The type is part of the key on purpose: a stale trackId of the wrong type
// (e.g. carried over by an image→video swap) splits into per-type rows
// instead of mixing media kinds in one row.
export function rowKeyForLayer(layer) {
  if (layer.trackId && GROUPABLE_TYPES.has(layer.type)) {
    return `track:${layer.trackId}:${layer.type}`;
  }
  return `solo:${layer.id}`;
}

// displayLayers: front-first (as rendered top→bottom). A grouped row sits
// where its BACKMOST member sits — anchoring on the frontmost one instead
// would yank the whole row to the top of the timeline every time a clip is
// added to it (a new box is always frontmost). Members render sorted by
// startFrame. After a row drag-reorder every row's members are contiguous in
// z-order, so both anchors agree there; only fresh adds differ.
export function groupLayersIntoRows(displayLayers) {
  const rows = [];
  const byKey = new Map();
  (displayLayers || []).forEach((layer, index) => {
    const key = rowKeyForLayer(layer);
    let row = byKey.get(key);
    if (!row) {
      row = {
        key,
        trackId: key.startsWith("track:") ? layer.trackId : null,
        type: layer.type,
        layers: [],
        isGroup: false,
        allLocked: false,
        allVisible: true,
        primary: layer,
      };
      byKey.set(key, row);
      rows.push(row);
    }
    row.layers.push(layer);
    row.anchorIndex = index; // last write wins → the backmost member's slot
  });
  rows.sort((a, b) => a.anchorIndex - b.anchorIndex);
  rows.forEach((row) => {
    row.layers.sort((a, b) => (a.startFrame || 0) - (b.startFrame || 0));
    row.isGroup = row.layers.length > 1;
    row.allLocked = row.layers.every((l) => l.locked === true);
    row.allVisible = row.layers.every((l) => l.visible !== false);
    row.primary = row.layers[0];
  });
  return rows;
}
